import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, MarkerF, OverlayView } from "@react-google-maps/api";
import useLocation from "../Hooks/useLocation";
import useCompanyId from "../Hooks/useCompanyId";
import { Badge, Button, Col, Container, Modal, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { isPointInPolygon } from "geolib";
import ChooseGeofenceModal from "./Modals/ChooseGeofenceModal";
import MsisdnOverviewRow from "./MsisdnOverviewRow";
import { MdPersonOff } from "react-icons/md";
import { getMultipleGeofences, getWorkOrdersForGeofences } from "../API/companyApi";
import ConditionalRender from "./ConditionalRender";
import useMyTranslation from "../Hooks/useMyTranslation";
import GeofencesDisplay from "../pages/GeofencePage/Components/GeofencesDisplay";
import activeUser from '../assets/active_user.png';
import polygonClipping from "polygon-clipping";

const DashboardMap = ({ overviews }) => {

    const { userLocation, isLoading } = useLocation()
    const companyId = useCompanyId()
    const t = useMyTranslation('pages.dashboard')

    const [msisdnsForGeofence, setMsisdnForGeofence] = useState({})
    const [geofences, setGeofences] = useState([])
    const [geofencesToChooseFrom, setGeofencesToChooseFrom] = useState([])
    const [selectedGeofence, setSelectedGeofence] = useState();
    const [currentZoom, setCurrentZoom] = useState(12);

    const mapRef = useRef(null);

    const getAllGeofencesFromOverviews = () => {
        return overviews.reduce((acc, overview) => acc.concat(overview.geofences.active), []);
    }

    const getAllUniqueGeofenceIds = () => {
        return [...new Set(getAllGeofencesFromOverviews().map(g => g.id))]
    }

    useEffect(() => {
        if (!companyId)
            return

        const fetchGeofences = async () => {
            const distinctGeofencesIds = getAllUniqueGeofenceIds()

            if (distinctGeofencesIds.length > 0) {
                const [geofencesResponse, workOrdersResponse] = await Promise.all([
                    getMultipleGeofences(distinctGeofencesIds, true),
                    getWorkOrdersForGeofences(distinctGeofencesIds)
                ])
                // console.log("geofs ", geofencesResponse)

                const geofencesWithWorkOrders = geofencesResponse.data.map(geofence => {
                    const workOrders = workOrdersResponse.data.filter(wo => wo.assignedGeofences.some(a => a.geofenceId === geofence.id))
                    return { ...geofence, workOrders: workOrders }
                })
                setGeofences(geofencesWithWorkOrders)
            }
        }

        fetchGeofences()
    }, [companyId, overviews]);

    const containerStyle = {
        width: '100%',
        height: '70vh',
        position: 'relative',
    };

    const center = useMemo(() => {
        //Eindhoven
        const defaultLocation =
        {
            lat: 51.441277,
            lng: 5.476341
        }

        return userLocation ? userLocation : defaultLocation

    }, [userLocation, isLoading])

    function geofenceIsValid(geofence) {
        const startValidity = new Date(geofence.validFrom)
        const endValidity = new Date(geofence.validTo)

        const now = new Date()

        return now > startValidity && now < endValidity
    }

    function optionsForGeofence(geofence) {
        const geofenceIsActive = geofenceIsValid(geofence)

        const activeGeofenceOptions = {
            strokeColor: "#539b4d",
            strokeOpacity: 0.8,
            strokeWeight: 3,
            fillColor: "#b3deb8",
            fillOpacity: 0.5
        }

        const inactiveGeofenceOptions = {
            strokeColor: "#FF0000",
            strokeOpacity: 0.8,
            strokeWeight: 1,
            fillColor: "#bc5c5c",
            fillOpacity: 0.5
        }

        return geofenceIsActive ? activeGeofenceOptions : inactiveGeofenceOptions
    }

    useEffect(() => {
        if (geofences.length > 0 && overviews.length > 0) {
            // Parse the JSON string and store the result in the state variable
            const geofenceData = overviews.reduce((acc, overview) => {
                overview.geofences.active.concat(overview.geofences.recent).forEach(geofence => {
                    if (!acc[geofence.id]) {
                        acc[geofence.id] = [];
                    }
                    acc[geofence.id].push({ ...geofence, msisdn: overview.msisdn });
                });
                return acc;
            }, {});

            // Check each geofence in the total geofence data
            geofences.forEach(geofence => {
                if (!geofenceData[geofence.id]) {
                    geofenceData[geofence.id] = [];
                }
            });

            setMsisdnForGeofence(geofenceData);
        }
    }, [geofences, overviews]);


    const handleClusterClick = (cluster) => {
        const bounds = cluster.bounds;

        //Get geofences that has MSISDNs inside and are inside the bounds of the cluster.
        const geofencesInBounds = geofences.filter(geofence => msisdnsForGeofence[geofence.id].length > 0).filter(geofence => {
            return geofence.points.some(point => {
                const latLng = new window.google.maps.LatLng(point.latitude, point.longitude);
                return bounds.contains(latLng);
            });
        });

        if (geofencesInBounds.length > 0) {
            if (geofencesInBounds.length === 1) {
                setSelectedGeofence(geofencesInBounds[0])
            }
            else {
                setGeofencesToChooseFrom(geofencesInBounds)
            }
        }
        else {
            const center = cluster.center
            const position = {
                latitude: center.lat(),
                longitude: center.lng()
            }
            handleClick(position)
        }
    }
    const handleMapClick = (event) => {
        const position = {
            latitude: event.latLng.lat(),
            longitude: event.latLng.lng()
        }
        handleClick(position)
    };

    const handleClick = (position) => {
        const clickedGeofences = geofences.filter(g => isPointInPolygon(position, g.points))

        if (clickedGeofences.length > 0) {
            if (clickedGeofences.length === 1) {
                setSelectedGeofence(clickedGeofences[0])
            }
            else {
                setGeofencesToChooseFrom(clickedGeofences)
            }
        }
    }

    // ----------------------------
    // HELPER FUNCTIONS
    // ----------------------------
    const getPolygonCentroid = polygon => {
        // polygon should be an array of linear rings
        if (!polygon || !Array.isArray(polygon) || !Array.isArray(polygon[0])) return null;

        const pts = polygon[0];
        if (!pts.length || typeof pts[0][0] !== 'number' || typeof pts[0][1] !== 'number') return null;

        let area = 0, x = 0, y = 0;
        for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
            const [x1, y1] = pts[j];
            const [x2, y2] = pts[i];
            const f = x1 * y2 - x2 * y1;
            area += f;
            x += (x1 + x2) * f;
            y += (y1 + y2) * f;
        }
        area *= 0.5;
        if (area === 0) return null;

        return { lng: x / (6 * area), lat: y / (6 * area) };
    };


    const getGeofenceCenter = id => {
        const g = geofences.find(g => g.id === id);
        if (!g?.points?.length) return null;
        const pts = g.points;
        return {
            lat: pts.reduce((s, p) => s + p.latitude, 0) / pts.length,
            lng: pts.reduce((s, p) => s + p.longitude, 0) / pts.length
        };
    };
    const toPolygon = g => [g.points.map(p => [p.longitude, p.latitude])];

    const getIntersectionCenter = ids => {
        const polys = ids.map(id => geofences.find(g => g.id === id))
            .filter(Boolean)
            .map(toPolygon);
        if (polys.length === 0) return null;

        let intersection = polys[0];
        for (let i = 1; i < polys.length; i++) {
            intersection = polygonClipping.intersection(intersection, polys[i]);
            // intersection can be null, number, or empty
            if (!intersection || !Array.isArray(intersection) || intersection.length === 0) return null;
        }

        // intersection might be a multi-polygon: pick the first valid polygon
        let poly = intersection.find(p => Array.isArray(p) && p.length > 0);
        if (!poly) return null;

        return getPolygonCentroid(poly);
    };


    // ----------------------------
    // BUILD GROUPS (EXACT MATCH)
    // ----------------------------
    const groups = useMemo(() => {
        const combinationMap = new Map();

        overviews.forEach(o => {
            const ids = (o.geofences?.active ?? []).map(g => g.id).sort((a, b) => a - b);
            if (ids.length === 0) return;

            const key = ids.join("_"); // exact match key
            if (!combinationMap.has(key)) {
                combinationMap.set(key, { geofenceIds: ids, people: [] });
            }
            combinationMap.get(key).people.push(o);
        });

        return Array.from(combinationMap.values());
    }, [overviews]);

    // ----------------------------
    // RENDER MARKERS
    // ----------------------------
    const renderMarkers = () => {
        if (!geofences.length || !groups.length) return null;

        return groups.map((group, idx) => {
            let loc = getIntersectionCenter(group.geofenceIds);

            if (!loc) {
                const centers = group.geofenceIds.map(getGeofenceCenter).filter(Boolean);
                loc = centers.length ? {
                    lat: centers.reduce((s, c) => s + c.lat, 0) / centers.length,
                    lng: centers.reduce((s, c) => s + c.lng, 0) / centers.length
                } : null;
            }

            if (!loc) return null;

            return (
                <React.Fragment key={idx}>
                    <MarkerF
                        position={loc}
                        icon={{
                            url: activeUser,
                            scaledSize: new window.google.maps.Size(40, 40),
                            anchor: new window.google.maps.Point(20, 20)
                        }}
                    />
                    <OverlayView
                        position={loc}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                        <div
                            style={{
                                background: "red",
                                borderRadius: "50%",
                                color: "white",
                                width: "16px",
                                height: "16px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "12px",
                                fontWeight: "bold",
                                transform: "translate(7px, -22px)"
                            }}
                        >
                            {group.people.length} {/* badge = exact # of people */}
                        </div>
                    </OverlayView>
                </React.Fragment>
            );
        });
    };



    const overviewsForSelected = selectedGeofence ? overviews.filter(overview => msisdnsForGeofence[selectedGeofence.id].map(info => info.msisdn.id).includes(overview.msisdn.id)) : [];

    return (
        <>
            <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                onLoad={map => mapRef.current = map}
                onZoomChanged={() => mapRef.current && setCurrentZoom(mapRef.current.getZoom())}
                zoom={12}
                options={{
                    disableDefaultUI: true,
                    zoomControl: true,
                    clickableIcons: false,
                    mapTypeControl: true,
                    styles: [
                        {
                            featureType: 'poi',
                            elementType: 'labels',
                            stylers: [
                                {
                                    visibility: 'off',
                                },
                            ]
                        },
                        {
                            featureType: 'transit.station',
                            stylers: [
                                {
                                    visibility: 'off',
                                },
                            ],
                        },
                    ]
                }}>

                {renderMarkers()}


                <GeofencesDisplay
                    geofences={geofences}
                    colorIfActive={true}
                    zoom={currentZoom}
                    onRegularGeofenceClick={(_, e) => handleMapClick(e)} baseGeofenceStyle={{}}
                    isDashboardMap={true} />

                {/*{geofences && geofences.map((geofence, index) =>*/}
                {/*    <GeofencePolygon*/}
                {/*        key={index}*/}
                {/*        geofence={geofence}*/}
                {/*        geofenceOptions={optionsForGeofence(geofence)}*/}
                {/*        onRegularGeofenceClick={(g, e) => handleMapClick(e)}*/}
                {/*    />*/}
                {/*)}*/}

                {/*{geofences && geofences.map((geofence) => {*/}
                {/*    // Map the points array to LatLngLiteral objects*/}
                {/*    const paths = geofence.points.map(point => ({*/}
                {/*        lat: point.latitude,*/}
                {/*        lng: point.longitude,*/}
                {/*    }));*/}

                {/*    return (*/}
                {/*        <PolygonF*/}
                {/*            key={geofence.id}*/}
                {/*            paths={paths}*/}
                {/*            options={optionsForGeofence(geofence)}*/}
                {/*            onClick={(e) => handleMapClick(e)}*/}
                {/*        />*/}
                {/*    );*/}
                {/*})}*/}

                {/*{overviews && geofences && geofences.length > 0 && <MarkerClustererF gridSize={14} onClick={handleClusterClick} zoomOnClick={false}>*/}
                {/*    {clusterer =>*/}
                {/*        overviews.filter(x => x.geofences.active.length > 0).map((overview, index) => {*/}
                {/*            const geofence = geofences.find(geofence => geofence.id === overview.geofences.active[0].id);*/}

                {/*            return (*/}
                {/*                <MarkerF*/}
                {/*                    key={index}*/}
                {/*                    position={centerOfGeofence(geofence.points)}*/}
                {/*                    clusterer={clusterer}*/}
                {/*                    icon={{*/}
                {/*                        url: require('../assets/marker-one.png'),*/}
                {/*                        anchor: new window.google.maps.Point(25, 35)*/}
                {/*                    }}*/}
                {/*                    onClick={(e) => handleMapClick(e)}*/}
                {/*                />*/}
                {/*            )*/}
                {/*        })*/}
                {/*    }*/}
                {/*</MarkerClustererF>}*/}

            </GoogleMap>

            <ChooseGeofenceModal
                active={geofencesToChooseFrom.length > 1}
                geofences={geofencesToChooseFrom}
                title='Choose geofence'
                onCancel={() => setGeofencesToChooseFrom([])}
                onSelect={(geofence) => setSelectedGeofence(geofence)}
            />

            {selectedGeofence &&
                <Modal size='xl' show={selectedGeofence} onHide={() => { setSelectedGeofence(null) }}>
                    <Modal.Header className='modal-header' closeButton>
                        <Modal.Title>
                            {selectedGeofence.name}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Container className='p-0' fluid>
                            {
                                overviewsForSelected.length > 0 ?
                                    <table>
                                        <thead>
                                            <tr>
                                                <th width={30} />
                                                <th className={'text-start'}>{t('employee')}</th>
                                                <th className={'text-start'}>{t('phone')}</th>
                                                <th>{t('startWorkday')}</th>
                                                <th>{t('endWorkday')}</th>
                                                <th>{t('currentWorkLocation')}</th>
                                                <th>{t('previousWorkLocations')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {overviewsForSelected.map(overview => {
                                                return <MsisdnOverviewRow key={overview.msisdn.id} overview={overview}
                                                    showGeofenceStatus={false}
                                                    msisdnStatusPlacement='bottom' />
                                            })}
                                        </tbody>
                                    </table>

                                    :
                                    <>
                                        <Row>
                                            <Col xs={1}>
                                                <MdPersonOff size={50} />
                                            </Col>
                                            <Col className='center-v ps-4'>
                                                <span>There are no MSISDNs in the geofence at the moment.</span>
                                            </Col>
                                        </Row>
                                    </>

                            }
                            {/* <div className={'caption my-2'}>Work orders</div>
                            <ConditionalRender condition={selectedGeofence.workOrders.length > 0}
                                fallback={<small>No work orders assigned</small>}>
                                {selectedGeofence.workOrders.map(wo =>
                                    <OverlayTrigger key={wo.id} overlay={<Tooltip>{wo.key}</Tooltip>}>
                                        <Badge bg='primary' className='me-2' style={{ cursor: 'default' }}>{wo.name}</Badge>
                                    </OverlayTrigger>
                                )}
                            </ConditionalRender> */}
                        </Container>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant='secondary' onClick={() => setSelectedGeofence(null)}>Close</Button>
                    </Modal.Footer>
                </Modal>}

        </>
    );

};



export default DashboardMap;